import useIsPast from '@discover-data/stores/hooks/useIsPast';
import type { AvailabilitySlot } from '@discover-data/stores/restaurant/model/types';
import { dayOfMonth, dayOfWeek, isNear } from '@discover-lib/dateFormats';
import { cn } from '@discover-lib/utils';
import Badge from '@discover-ui/badge';
import Button, { type ButtonProps } from '@discover-ui/button';
import AvailabilityStatus from '@discover-components/availability/status';
import AvailabilitySlotCountdownMessage from './countdownMessage';

export type AvailabilitySlotProps = AvailabilitySlot & {
  cutOffTime?: string | null;
  timeZone?: string | null;
  showCountdownMessage?: boolean;
  badge?: string;
  badgeClassName?: string;
  onSelect?: (slot: AvailabilitySlot) => void;
  selected?: boolean;
  showDate?: boolean;
  status: 'available' | 'unavailable' | 'processing' | 'sold' | 'closed' | 'placeholder';
  hasMultipleTimes?: boolean;
  availableLabel?: string;
} & Omit<ButtonProps, 'children' | 'onClick' | 'disabled' | 'kind'>;

export default function AvailabilitySlot({
  date,
  time,
  cutOffTime,
  timeZone,
  showCountdownMessage = false,
  badge,
  badgeClassName,
  onSelect,
  selected,
  showDate = true,
  status,
  hasMultipleTimes = false,
  availableLabel = '',
  className,
  ...props
}: AvailabilitySlotProps) {
  const isPast = useIsPast(cutOffTime);
  const isAvailable =
    status === 'available' && (hasMultipleTimes || (!hasMultipleTimes && !isPast));
  const showBadge = isAvailable && !!badge;
  const countdownMessageVisible =
    isAvailable && showCountdownMessage && !!timeZone && !!cutOffTime && isNear(cutOffTime);

  return (
    <>
      <Button
        className={cn(
          'sm:text-2xs flex aspect-square w-full flex-col items-center justify-center gap-1 px-1 pb-3 pt-2 text-[2.5vw] font-normal leading-[2.5vw] text-blue-400 sm:gap-1 sm:px-2 sm:pb-4 sm:pt-3 lg:h-fit',
          'mb-[calc(1.25vw+2px)] lg:mb-[10px]', // margin for half the badge height
          {
            'bg-blue-50': !isAvailable,
          },
          className
        )}
        disabled={!isAvailable}
        kind={selected && isAvailable ? 'cta' : 'normal'}
        onClick={() => isAvailable && onSelect && onSelect({ date, time })}
        {...props}>
        {showDate && !!date && (
          <span
            className={cn({
              'text-white': selected && isAvailable,
            })}>
            {dayOfWeek(date)}
          </span>
        )}
        <span
          className={cn(
            'relative flex items-center justify-center self-stretch text-[2.5vw] sm:text-xs',
            {
              'text-white font-bold': isAvailable,
            }
          )}>
          <AvailabilityStatus availableLabel={availableLabel} status={status} />
        </span>
        {showDate && !!date && (
          <span
            className={cn({
              'text-white': selected && isAvailable,
            })}>
            {dayOfMonth(date)}
          </span>
        )}
        {showBadge && (
          <Badge
            kind="secondary"
            className={cn(
              'sm:text-2xs absolute bottom-0 translate-y-1/2 text-[1.8vw] font-bold',
              badgeClassName
            )}>
            {badge}
          </Badge>
        )}
      </Button>
      {countdownMessageVisible && cutOffTime && (
        <AvailabilitySlotCountdownMessage cutOffTime={cutOffTime} />
      )}
    </>
  );
}
